import React, { useCallback, useEffect, useRef, useState } from 'react';
import apiService from '../services/api';
import { PageResponse, Structure, StructureRequest } from '../types';
import Pagination from '../components/Pagination';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  BuildingOffice2Icon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  BuildingLibraryIcon,
  TableCellsIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';

type StructureViewMode = 'table' | 'cards';

const PAGE_SIZE = 15;

// ── Palette de couleurs pour les cards (basée sur l'index) ───────────────────
const CARD_COLORS = [
  { icon: 'bg-primary-100', iconText: 'text-primary-600', accent: 'bg-primary-500' },
  { icon: 'bg-blue-100',    iconText: 'text-blue-600',    accent: 'bg-blue-500'    },
  { icon: 'bg-violet-100',  iconText: 'text-violet-600',  accent: 'bg-violet-500'  },
  { icon: 'bg-amber-100',   iconText: 'text-amber-600',   accent: 'bg-amber-500'   },
  { icon: 'bg-teal-100',    iconText: 'text-teal-600',    accent: 'bg-teal-500'    },
  { icon: 'bg-rose-100',    iconText: 'text-rose-600',    accent: 'bg-rose-500'    },
];

const getCardColor = (index: number) => CARD_COLORS[index % CARD_COLORS.length];

// ── Composant principal ────────────────────────────────────────────────────────

const Structures: React.FC = () => {
  const [result, setResult] = useState<PageResponse<Structure>>({
    content: [], page: 0, size: PAGE_SIZE, totalElements: 0, totalPages: 0, first: true, last: true,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isDebouncing, setIsDebouncing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingStructure, setEditingStructure] = useState<Structure | null>(null);
  const [form, setForm] = useState<StructureRequest>({ nom: '', sigle: '', description: '' });
  const [viewMode, setViewMode] = useState<StructureViewMode>('table');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [structureToDelete, setStructureToDelete] = useState<Structure | null>(null);
  const [error, setError] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const fetchStructures = useCallback(async (page: number, searchTerm: string) => {
    setIsLoading(true);
    try {
      const data = await apiService.getStructuresPage({
        page,
        size: PAGE_SIZE,
        search: searchTerm || undefined,
      });
      setResult(data);
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStructures(currentPage, search);
  }, [currentPage, search, fetchStructures]);

  // ── Recherche avec debounce ───────────────────────────────────────────────
  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    setIsDebouncing(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(value);
      setCurrentPage(0);
      setIsDebouncing(false);
    }, 400);
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearch('');
    setCurrentPage(0);
    setIsDebouncing(false);
  };

  // ── CRUD ──────────────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      if (editingStructure) {
        await apiService.updateStructure(editingStructure.id, form);
      } else {
        await apiService.createStructure(form);
      }
      await fetchStructures(currentPage, search);
      setShowModal(false);
      setEditingStructure(null);
      setForm({ nom: '', sigle: '', description: '' });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Une erreur est survenue');
    }
  };

  const handleDelete = async () => {
    if (!structureToDelete) return;
    try {
      await apiService.deleteStructure(structureToDelete.id);
      await fetchStructures(currentPage, search);
      setShowDeleteModal(false);
      setStructureToDelete(null);
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
    }
  };

  const openEditModal = (structure: Structure) => {
    setEditingStructure(structure);
    setForm({
      nom: structure.nom,
      sigle: structure.sigle || '',
      description: structure.description || '',
    });
    setError('');
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingStructure(null);
    setForm({ nom: '', sigle: '', description: '' });
    setError('');
    setShowModal(true);
  };

  return (
    <div className="space-y-6">

      {/* ── En-tête ──────────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Structures</h1>
          <p className="mt-1 text-sm text-gray-500">
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block h-2 w-2 rounded-full bg-primary-500" />
              {result.totalElements} structure{result.totalElements > 1 ? 's' : ''} au total
            </span>
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Sélecteur de vue */}
          <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1">
            <button
              onClick={() => setViewMode('table')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-150 ${
                viewMode === 'table' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <TableCellsIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Tableau</span>
            </button>
            <button
              onClick={() => setViewMode('cards')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-150 ${
                viewMode === 'cards' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Squares2X2Icon className="h-4 w-4" />
              <span className="hidden sm:inline">Cartes</span>
            </button>
          </div>
          <button
            onClick={openCreateModal}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold rounded-xl shadow-sm transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          >
            <PlusIcon className="h-4 w-4" />
            Nouvelle structure
          </button>
        </div>
      </div>

      {/* ── Barre de recherche ────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
        <div className="relative max-w-xl">
          <MagnifyingGlassIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Rechercher par nom ou sigle…"
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="block w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm transition-colors duration-150 placeholder:text-gray-400"
          />
          {/* Spinner debounce */}
          {isDebouncing && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <div className="h-4 w-4 border-2 border-primary-300 border-t-primary-600 rounded-full animate-spin" />
            </div>
          )}
          {searchInput && !isDebouncing && (
            <button
              onClick={clearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Effacer la recherche"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
        </div>
        {search && (
          <p className="mt-2 text-xs text-gray-500">
            {result.totalElements} résultat{result.totalElements > 1 ? 's' : ''} pour «{' '}
            <span className="font-medium text-gray-700">{search}</span> »
          </p>
        )}
      </div>

      {/* ── État vide commun ─────────────────────────────────────────────── */}
      {!isLoading && result.content.length === 0 && (
        <div className="flex flex-col items-center justify-center py-24 px-6 text-center bg-white rounded-2xl border border-gray-100 shadow-sm">
          <div className="h-16 w-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
            <BuildingLibraryIcon className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-sm font-semibold text-gray-800 mb-1">
            {search ? 'Aucun résultat trouvé' : 'Aucune structure enregistrée'}
          </h3>
          <p className="text-sm text-gray-400 max-w-xs">
            {search
              ? `Aucune structure ne correspond à « ${search} ». Modifiez votre recherche.`
              : 'Commencez par créer une première structure en cliquant sur le bouton ci-dessus.'}
          </p>
          {search && (
            <button onClick={clearSearch} className="mt-4 text-sm text-primary-600 hover:text-primary-700 font-medium">
              Effacer la recherche
            </button>
          )}
        </div>
      )}

      {/* ── Vue Tableau (défaut) ──────────────────────────────────────────── */}
      {viewMode === 'table' && (
        isLoading ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden animate-pulse">
            <div className="h-11 bg-gray-100 border-b" />
            {[...Array(8)].map((_, i) => (
              <div key={i} className="flex gap-4 px-5 py-3.5 border-b items-center">
                <div className="h-4 bg-gray-200 rounded w-1/3" />
                <div className="h-5 bg-gray-100 rounded-full w-16" />
                <div className="h-3 bg-gray-100 rounded w-1/2" />
                <div className="ml-auto flex gap-2">
                  <div className="h-7 w-16 bg-gray-100 rounded-lg" />
                  <div className="h-7 w-20 bg-gray-100 rounded-lg" />
                </div>
              </div>
            ))}
          </div>
        ) : result.content.length > 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Nom</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Sigle</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide hidden md:table-cell">Description</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {result.content.map((structure, index) => {
                    const colors = getCardColor(index);
                    return (
                      <tr key={structure.id} className={`hover:bg-gray-50 transition-colors ${index % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-3">
                            <div className={`flex-shrink-0 h-8 w-8 ${colors.icon} rounded-lg flex items-center justify-center`}>
                              <BuildingOffice2Icon className={`h-4 w-4 ${colors.iconText}`} />
                            </div>
                            <span className="font-semibold text-gray-900">{structure.nom}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3.5">
                          {structure.sigle
                            ? <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-gray-100 text-gray-600 ring-1 ring-gray-200">{structure.sigle}</span>
                            : <span className="text-xs text-gray-300 italic">—</span>}
                        </td>
                        <td className="px-4 py-3.5 hidden md:table-cell">
                          <span className="text-xs text-gray-500 line-clamp-1 max-w-sm">
                            {structure.description ?? <span className="italic text-gray-300">Aucune description</span>}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              onClick={() => openEditModal(structure)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:border-primary-300 hover:text-primary-600 hover:bg-primary-50 transition-colors"
                            >
                              <PencilIcon className="h-3.5 w-3.5" />
                              Modifier
                            </button>
                            <button
                              onClick={() => { setStructureToDelete(structure); setShowDeleteModal(true); }}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:border-red-300 hover:text-red-600 hover:bg-red-50 transition-colors"
                            >
                              <TrashIcon className="h-3.5 w-3.5" />
                              Supprimer
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : null
      )}

      {/* ── Vue Cartes ────────────────────────────────────────────────────── */}
      {viewMode === 'cards' && (
        isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden animate-pulse">
                <div className="p-5 space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="h-11 w-11 bg-gray-200 rounded-xl flex-shrink-0" />
                    <div className="flex-1 space-y-2 pt-0.5">
                      <div className="h-4 bg-gray-200 rounded w-3/4" />
                      <div className="h-3 bg-gray-100 rounded w-1/3" />
                    </div>
                  </div>
                  <div className="h-3 bg-gray-100 rounded w-full" />
                  <div className="h-3 bg-gray-100 rounded w-4/5" />
                </div>
                <div className="px-5 py-3 border-t border-gray-100 flex justify-end gap-2">
                  <div className="h-8 w-20 bg-gray-100 rounded-lg" />
                  <div className="h-8 w-24 bg-gray-100 rounded-lg" />
                </div>
              </div>
            ))}
          </div>
        ) : result.content.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {result.content.map((structure, index) => {
              const colors = getCardColor(index);
              const descTruncated = structure.description
                ? structure.description.length > 100
                  ? structure.description.slice(0, 100) + '…'
                  : structure.description
                : null;
              return (
                <div
                  key={structure.id}
                  className="group relative bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 overflow-hidden flex flex-col"
                >
                  <div className={`h-1 w-full ${colors.accent}`} />
                  <div className="p-5 flex-1">
                    <div className="flex items-start gap-3">
                      <div className={`flex-shrink-0 h-11 w-11 ${colors.icon} rounded-xl flex items-center justify-center`}>
                        <BuildingOffice2Icon className={`h-5 w-5 ${colors.iconText}`} />
                      </div>
                      <div className="flex-1 min-w-0 pt-0.5">
                        <h3 className="text-sm font-bold text-gray-900 leading-tight truncate">{structure.nom}</h3>
                        {structure.sigle
                          ? <span className="mt-1 inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-gray-100 text-gray-600 ring-1 ring-gray-200">{structure.sigle}</span>
                          : <span className="mt-1 text-xs text-gray-300 italic">Pas de sigle</span>}
                      </div>
                    </div>
                    <div className="mt-4">
                      {descTruncated
                        ? <p className="text-xs text-gray-500 leading-relaxed">{descTruncated}</p>
                        : <p className="text-xs text-gray-300 italic">Aucune description</p>}
                    </div>
                  </div>
                  <div className="px-5 py-3.5 border-t border-gray-100 bg-gray-50/60 flex items-center justify-end gap-2">
                    <button
                      onClick={() => openEditModal(structure)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:border-primary-300 hover:text-primary-600 hover:bg-primary-50 transition-colors duration-150"
                    >
                      <PencilIcon className="h-3.5 w-3.5" />
                      Modifier
                    </button>
                    <button
                      onClick={() => { setStructureToDelete(structure); setShowDeleteModal(true); }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:border-red-300 hover:text-red-600 hover:bg-red-50 transition-colors duration-150"
                    >
                      <TrashIcon className="h-3.5 w-3.5" />
                      Supprimer
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : null
      )}

      {/* ── Pagination ────────────────────────────────────────────────────── */}
      <Pagination
        page={result.page}
        totalPages={result.totalPages}
        totalElements={result.totalElements}
        size={result.size}
        onPageChange={setCurrentPage}
      />

      {/* ══════════════════════════════════════════════════════════════════════
          Modal Créer / Modifier
      ══════════════════════════════════════════════════════════════════════ */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">

            {/* Header vert */}
            <div className="bg-primary-600 px-6 py-5 flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-white">
                  {editingStructure ? 'Modifier la structure' : 'Nouvelle structure'}
                </h3>
                <p className="text-xs text-primary-200 mt-0.5">
                  {editingStructure
                    ? editingStructure.nom
                    : 'Remplissez les informations ci-dessous'}
                </p>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="p-1.5 text-primary-200 hover:text-white hover:bg-primary-500 rounded-lg transition-colors"
                aria-label="Fermer"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Corps */}
            <div className="p-6">
              {error && (
                <div className="mb-5 flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                  <ExclamationTriangleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">

                {/* Nom */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                    Nom complet <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="ex : Ministère de l'Économie"
                    value={form.nom}
                    onChange={(e) => setForm({ ...form, nom: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  />
                </div>

                {/* Sigle */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                    Sigle
                    <span className="ml-1.5 text-gray-400 font-normal normal-case tracking-normal">(facultatif)</span>
                  </label>
                  <input
                    type="text"
                    placeholder="ex : MINECO"
                    value={form.sigle}
                    onChange={(e) => setForm({ ...form, sigle: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                    Description
                    <span className="ml-1.5 text-gray-400 font-normal normal-case tracking-normal">(facultatif)</span>
                  </label>
                  <textarea
                    placeholder="Décrivez brièvement la mission de cette structure…"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors resize-none"
                  />
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 pt-2 border-t border-gray-100 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2.5 text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 rounded-xl shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                  >
                    {editingStructure ? 'Enregistrer' : 'Créer la structure'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          Modal Suppression
      ══════════════════════════════════════════════════════════════════════ */}
      {showDeleteModal && structureToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">

            {/* Header rouge */}
            <div className="bg-red-600 px-6 py-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 bg-red-500 rounded-xl flex items-center justify-center">
                  <ExclamationTriangleIcon className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-base font-bold text-white">Supprimer la structure</h3>
              </div>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="p-1.5 text-red-200 hover:text-white hover:bg-red-500 rounded-lg transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Corps */}
            <div className="p-6">
              <p className="text-sm text-gray-600 leading-relaxed">
                Vous êtes sur le point de supprimer la structure{' '}
                <span className="font-semibold text-gray-900">"{structureToDelete.nom}"</span>.
                Cette action est <span className="font-semibold text-red-600">irréversible</span>.
                Tous les utilisateurs liés à cette structure perdront leur affectation.
              </p>

              {/* Fiche de la structure */}
              <div className="mt-4 p-4 bg-red-50 border border-red-100 rounded-xl flex items-center gap-3">
                <div className="flex-shrink-0 h-10 w-10 bg-red-100 rounded-xl flex items-center justify-center">
                  <BuildingOffice2Icon className="h-5 w-5 text-red-500" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-800 truncate">{structureToDelete.nom}</p>
                  {structureToDelete.sigle && (
                    <span className="text-xs font-medium text-gray-500">{structureToDelete.sigle}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 pb-6 flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                className="px-5 py-2.5 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-xl shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              >
                Oui, supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Structures;
